import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';

import { useSlackGuestAccessMutation } from '../../hooks/useSettingsSwitchMutations';
import { ACTION_CONFIG, type PendingAction } from './types';

export function useSlackIntegration() {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingAction | null>(null);

  const statusQuery = useQuery({
    ...orpc.slack.getInstallStatus.queryOptions({ input: {} }),
  });

  const slackWorkspaceBaseUrl =
    (statusQuery.data && 'teamUrl' in statusQuery.data ? statusQuery.data.teamUrl : null)?.replace(/\/+$/, '') ?? null;

  const installed = statusQuery.data?.installed === true;

  const linksQuery = useQuery({
    ...orpc.slack.listUserLinks.queryOptions({ input: {} }),
    enabled: installed,
  });

  const bindingsQuery = useQuery({
    ...orpc.slack.listChannelBindings.queryOptions({ input: {} }),
    enabled: installed,
  });

  const settingsQuery = useQuery({
    ...orpc.settings.get.queryOptions({ input: {} }),
    enabled: installed,
  });

  const invalidateLinks = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: orpc.slack.listUserLinks.queryOptions({ input: {} }).queryKey,
    });
  }, [orpc.slack, queryClient]);

  const invalidateBindings = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: orpc.slack.listChannelBindings.queryOptions({ input: {} }).queryKey,
    });
  }, [orpc.slack, queryClient]);

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: orpc.slack.key() });
  }, [orpc.slack, queryClient]);

  const guestAccessMutation = useSlackGuestAccessMutation();

  const unlinkMutation = useMutation(
    orpc.slack.unlinkUser.mutationOptions({
      onSuccess: () => {
        setPending(null);
        invalidateLinks();
        toast.success('User disconnected');
      },
      onError: () => toast.error('Failed to disconnect user'),
    }),
  );

  const unbindMutation = useMutation(
    orpc.slack.unbindChannel.mutationOptions({
      onSuccess: (result) => {
        setPending(null);
        invalidateBindings();
        if (result.deleted) {
          toast.success('Channel disconnected');
        } else {
          toast.warning('Channel was already disconnected');
        }
      },
      onError: () => toast.error('Failed to disconnect channel'),
    }),
  );

  const uninstallMutation = useMutation(
    orpc.slack.uninstall.mutationOptions({
      onSuccess: () => {
        invalidateAll();
        toast.success('Slack app uninstalled');
      },
      onError: () => toast.error('Failed to uninstall'),
    }),
  );

  const actionPending = unlinkMutation.isPending || unbindMutation.isPending;
  const pendingConfig = pending ? ACTION_CONFIG[pending.variant] : null;

  const confirmAction = useCallback((): void => {
    if (!pending) return;
    if (pending.variant === 'unlink') {
      unlinkMutation.mutate({ teamId: pending.teamId, slackUserId: pending.slackUserId });
    } else {
      unbindMutation.mutate({ teamId: pending.teamId, channelId: pending.channelId });
    }
  }, [pending, unlinkMutation, unbindMutation]);

  return {
    statusQuery,
    linksQuery,
    bindingsQuery,
    settingsQuery,
    slackWorkspaceBaseUrl,
    guestAccessMutation,
    uninstallMutation,
    pending,
    setPending,
    actionPending,
    confirmAction,
    pendingConfig,
  };
}
